import { useState, useMemo, useEffect } from "react";
import { Check, X, Edit2, Percent } from "lucide-react";
import { useAdminData } from "../../hooks/useAdminData";
import { settings as settingsApi, fees as feesApi } from "../../api/endpoints";
import { AddToast } from "../../components/feedback/Toast";
import { OrgAvatar } from "../../components/ui/Avatar";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { PageHeader } from "../../components/ui/PageHeader";
import { SectionLabel } from "../../components/ui/SectionLabel";
import { F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Fee Management view ───────────────────────────────────────────────────────

export function FeeManagementView({ addToast }: { addToast: AddToast }) {
  const { ADMIN_ORGS, refetchOrgs } = useAdminData();
  const [defaultFee, setDefaultFee] = useState("10");
  const [editingOrg, setEditingOrg] = useState<string | null>(null);
  const [editVal, setEditVal] = useState("");

  useEffect(() => {
    settingsApi.get().then((s) => setDefaultFee(String(s.platform_fee))).catch(() => { });
  }, []);

  const customFees = useMemo(() => Object.fromEntries(
    ADMIN_ORGS.filter((o) => o.customFee !== undefined).map((o) => [o.id, String(o.customFee)])
  ), [ADMIN_ORGS]);

  function saveDefaultFee() {
    settingsApi.update({ platform_fee: Number(defaultFee) })
      .then(() => addToast("Default fee updated", "success"))
      .catch(() => addToast("Failed to update default fee", "error"));
  }
  function saveCustomFee(orgId: string, orgName: string, value: string) {
    feesApi.updateOrgFee(orgId, Number(value))
      .then(() => { refetchOrgs(); setEditingOrg(null); addToast(`Custom fee set for ${orgName}`, "success"); })
      .catch(() => addToast("Failed to set custom fee", "error"));
  }
  function removeCustomFee(orgId: string) {
    feesApi.updateOrgFee(orgId, null)
      .then(() => { refetchOrgs(); addToast("Custom fee removed", "info"); })
      .catch(() => addToast("Failed to remove custom fee", "error"));
  }

  return (
    <div>
      <PageHeader title="Fee Management" sub="Control platform fees per organisation or globally" />

      <div className="rg-split" style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "20px" }}>
        {/* Default fee */}
        <div>
          <Card style={{ padding: "22px" }}>
            <SectionLabel>Global default fee</SectionLabel>
            <p style={{ fontSize: "12px", color: T.sub, fontFamily: F, marginBottom: "16px" }}>Applied to all organisations without a custom fee.</p>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <div style={{ flex: 1, display: "flex", alignItems: "center", gap: "0", border: `1px solid ${T.border}`, borderRadius: "8px", overflow: "hidden" }}>
                <input value={defaultFee} onChange={(e) => setDefaultFee(e.target.value)} type="number" min={0} max={30}
                  style={{ flex: 1, border: "none", outline: "none", padding: "9px 12px", fontSize: "20px", fontFamily: "'Space Mono', monospace", fontWeight: 700, color: T.text, background: "transparent", textAlign: "center" }} />
                <span style={{ padding: "0 14px", fontSize: "18px", color: T.sub, fontFamily: F }}>%</span>
              </div>
            </div>
            <Btn variant="primary" onClick={saveDefaultFee} style={{ width: "100%", justifyContent: "center" }}>Save default fee</Btn>

            <div style={{ marginTop: "20px", padding: "12px 14px", background: T.s2, borderRadius: "8px" }}>
              <p style={{ fontSize: "11px", color: T.sub, fontFamily: F, lineHeight: 1.6 }}>
                The default fee is applied to platform payments when no custom rate is configured for an organisation. Changes take effect on the next payment cycle.
              </p>
            </div>
          </Card>

          <Card style={{ padding: "22px", marginTop: "14px" }}>
            <SectionLabel>Fee summary</SectionLabel>
            {[
              { label: "Default rate", value: `${defaultFee}%` },
              { label: "Orgs with custom rate", value: Object.keys(customFees).length.toString() },
              { label: "Lowest custom rate", value: Object.values(customFees).length > 0 ? `${Math.min(...Object.values(customFees).map(Number))}%` : "—" },
              { label: "Est. platform revenue", value: "SAR 942K / mo" },
            ].map((item, i, arr) => (
              <div key={item.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: i < arr.length - 1 ? `1px solid ${T.border}` : "none" }}>
                <p style={{ fontSize: "12px", color: T.sub, fontFamily: F }}>{item.label}</p>
                <p style={{ fontSize: "13px", fontWeight: 600, color: T.text, fontFamily: F }}>{item.value}</p>
              </div>
            ))}
          </Card>
        </div>

        {/* Per-org overrides */}
        <Card className="flat-phone" style={{ padding: "22px" }}>
          <SectionLabel>Per-organisation custom fees</SectionLabel>
          <p style={{ fontSize: "12px", color: T.sub, fontFamily: F, marginBottom: "16px" }}>Custom rates override the default for individual organisations. Useful for volume partners or strategic accounts.</p>
          <TableWrap><table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                {["Organisation", "Industry", "Total spend", "Rate applied", "Custom rate", "Actions"].map((h) => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ADMIN_ORGS.filter((o) => o.kyb === "Verified").map((o) => {
                const custom = customFees[o.id];
                const isEditing = editingOrg === o.id;
                return (
                  <tr key={o.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                    <td style={{ padding: "11px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <OrgAvatar initials={o.initials} color={o.color} size={26} />
                        <p style={{ fontSize: "12px", fontWeight: 600, color: T.text, fontFamily: F }}>{o.name}</p>
                      </div>
                    </td>
                    <td style={{ padding: "11px 12px", fontSize: "11px", color: T.sub }}>{o.industry}</td>
                    <td style={{ padding: "11px 12px", fontSize: "11px", color: T.text, fontFamily: "'Space Mono', monospace" }}>{o.totalSpend}</td>
                    <td style={{ padding: "11px 12px" }}>
                      {custom !== undefined
                        ? <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", background: "#dbeafe", color: "#1e40af", borderRadius: "9999px", padding: "2px 9px", fontSize: "11px", fontWeight: 700, fontFamily: F }}><Percent size={9} />{custom}%</span>
                        : <span style={{ fontSize: "11px", color: T.muted, fontFamily: F }}>Default ({defaultFee}%)</span>}
                    </td>
                    <td style={{ padding: "11px 12px" }}>
                      {isEditing ? (
                        <input value={editVal} onChange={(e) => setEditVal(e.target.value)} type="number" min={0} max={30} autoFocus
                          style={{ width: "60px", border: `1px solid ${T.border}`, borderRadius: "6px", padding: "4px 8px", fontSize: "12px", fontFamily: "'Space Mono', monospace", outline: "none", textAlign: "center" }} />
                      ) : (
                        <span style={{ fontSize: "12px", color: T.muted, fontFamily: F }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: "11px 12px" }}>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: "4px" }}>
                          <Btn variant="primary" size="xs" onClick={() => saveCustomFee(o.id, o.name, editVal)}><Check size={10} /></Btn>
                          <Btn variant="ghost" size="xs" onClick={() => setEditingOrg(null)}><X size={10} /></Btn>
                        </div>
                      ) : (
                        <div style={{ display: "flex", gap: "4px" }}>
                          <Btn variant="ghost" size="xs" onClick={() => { setEditingOrg(o.id); setEditVal(custom ?? defaultFee); }}><Edit2 size={10} /> {custom !== undefined ? "Edit" : "Set custom"}</Btn>
                          {custom !== undefined && <Btn variant="ghost" size="xs" onClick={() => removeCustomFee(o.id)}><X size={10} /></Btn>}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></TableWrap>
        </Card>
      </div>
    </div>
  );
}
